import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { auth } from "../firebase";
import {
  onAuthStateChanged,
  getRedirectResult,
  getAdditionalUserInfo,
  User as FirebaseUser,
  UserCredential,
  updateProfile,
  deleteUser,
  signOut,
} from "firebase/auth";
import { apiService } from "../services/apiService";
import {
  tryRecoverMicrosoftRedirectAfterAccountExistsError,
  ACCOUNT_EXISTS_MS_REDIRECT_RECOVERY_KEY,
  parseSocialConflict409Payload,
  unlinkSessionProviderIfNotCanonical,
  clearPendingSocialRedirectProvider,
  getPendingSocialRedirectProvider,
} from "../services/authService";
import {
  handleWebViewLoginSuccess,
  isWebView,
  isViewRNativeAuthWebView,
  forceExtractFirebaseToken,
} from "../utils/webViewUtils";
import { notifyAuthSuccessToServer } from "../services/webViewService";
import type { User } from "../types";
import type { LoginMethod } from "../types/user";
import {
  BLOCKED_ACCOUNT_MESSAGE,
  mapLoginErrorMessage,
  mapSocialEmailConflictMessage,
  SERVICE_UNAVAILABLE_MESSAGE,
  SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE,
  USER_INFO_CHECK_FAILED_MESSAGE,
  USER_INFO_UNAVAILABLE_MESSAGE,
} from "../utils/loginAuthErrors";
import { sseService } from "../services/sseService";
import {
  AUTH_PROVIDER_PROFILE_UPDATED_EVENT,
  resolveAuthProfilePhotoURL,
  syncAuthProviderProfileFromCredential,
  type AuthProviderProfileUpdatedDetail,
} from "../services/authProviderProfile";
import { isGuestAuthEnabled } from "../utils/guestAuth";
import { isDesktopSocialPairingLocation } from "../services/desktopPairingAuth";
import {
  beginAuthSession,
  clearAuthSession,
  ensureAuthSession,
  ensureStoredAuthSession,
  getAuthSessionExpiry,
  recordAuthSessionActivity,
  type AuthSessionExpiryReason,
} from "../utils/authSessionPolicy";

// AuthContext value type
interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  redirectSignInResult?: AuthRedirectSignInResult | null;
  blockCheckVerified?: boolean;
  loginAsGuestUser: (provider?: string) => Promise<void>;
  logout: () => Promise<void>;
  setError: (error: string | null) => void;
  checkUserBlocked: () => Promise<boolean>;
}

export interface AuthRedirectSignInResult {
  uid: string;
  provider: "google" | "microsoft" | "apple";
  completedAt: number;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const isDev = process.env.NODE_ENV === "development";

const DEFAULT_DISPLAY_NAME = "User01";
const APPLE_SIGNUP_DISPLAY_NAME_KEY = "appleSignUpDisplayName";

/** Guest 세션 토큰. 서버 `modules/auth.py`의 GUEST_TOKENS와 같은 값이어야 한다. */
const GUEST_SESSION_TOKEN =
  "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJodHRwczovL3NlY3VyZXRva2VuLmdvb2dsZS5jb20vdGVzdCIsImF1ZCI6InRlc3QiLCJ1c2VyX2lkIjoiZ3Vlc3QtdWlkLTEyMzQiLCJpYXQiOjE2MzU1OTcyMDAsImV4cCI6MTYzNTYwMDgwMH0.guest-signature";

/** 예전 빌드가 저장했을 수 있는 guest 토큰 값. */
const LEGACY_GUEST_TOKENS = ["guest-token-1234", "guest-jwt-token"];

/** 저장된 토큰이 guest 세션 토큰인지 확인한다. */
export const isGuestSessionToken = (
  token: string | null | undefined,
): boolean => {
  if (!token) return false;
  return (
    LEGACY_GUEST_TOKENS.includes(token) ||
    token.includes("guest-signature") ||
    token.startsWith("guest:")
  );
};

const readStoredAuthToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return (
    localStorage.getItem("firebaseToken") ||
    sessionStorage.getItem("firebaseToken")
  );
};

/**
 * Guest 세션 사용자.
 * 최초 guest 로그인과 새로고침 복구가 같은 값을 쓰도록 한 곳에서 만든다.
 */
export const createGuestSessionUser = (): User => ({
  uid: "guest-uid-1234",
  email: "guest@example.com",
  displayName: "Guest user",
  photoURL: "/Images/Icons/GuestUser.png",
  token: GUEST_SESSION_TOKEN,
  isGuest: true,
});

const getFirstNonEmptyString = (...values: Array<unknown>): string | null => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return null;
};

const getEmailLocalPart = (email?: string | null): string | null => {
  if (!email) return null;
  const localPart = email.split("@")[0]?.trim();
  return localPart || null;
};

const getFirebaseProviderDisplayName = (
  firebaseUser: FirebaseUser,
): string | null =>
  getFirstNonEmptyString(
    ...firebaseUser.providerData.map((data) => data.displayName),
  );

const getFirebaseResolvedEmail = (firebaseUser: FirebaseUser): string | null =>
  getFirstNonEmptyString(
    firebaseUser.email,
    ...firebaseUser.providerData.map((data) => data.email),
  );

const getServerDisplayName = (response: unknown): string | null => {
  const root = response as
    | {
        displayName?: unknown;
        display_name?: unknown;
        name?: unknown;
        user?: {
          displayName?: unknown;
          display_name?: unknown;
          name?: unknown;
        };
      }
    | null
    | undefined;

  return getFirstNonEmptyString(
    root?.user?.displayName,
    root?.user?.display_name,
    root?.user?.name,
    root?.displayName,
    root?.display_name,
    root?.name,
  );
};

const getServerPhotoURL = (response: unknown): string | null => {
  const root = response as
    | {
        photoURL?: unknown;
        photo_url?: unknown;
        user?: { photoURL?: unknown; photo_url?: unknown };
      }
    | null
    | undefined;

  return getFirstNonEmptyString(
    root?.user?.photoURL,
    root?.user?.photo_url,
    root?.photoURL,
    root?.photo_url,
  );
};

const syncFirebaseDisplayName = async (
  firebaseUser: FirebaseUser,
  displayName?: string | null,
): Promise<void> => {
  const normalizedDisplayName = getFirstNonEmptyString(displayName);
  if (!normalizedDisplayName) return;
  if (firebaseUser.displayName === normalizedDisplayName) return;

  await updateProfile(firebaseUser, { displayName: normalizedDisplayName });
};

const getPendingAppleDisplayName = (): string | null =>
  getFirstNonEmptyString(sessionStorage.getItem(APPLE_SIGNUP_DISPLAY_NAME_KEY));

const clearPendingAppleDisplayName = (): void => {
  sessionStorage.removeItem(APPLE_SIGNUP_DISPLAY_NAME_KEY);
};

const resolveSocialLoginMethod = (
  providerId: string | null | undefined,
): AuthRedirectSignInResult["provider"] | null => {
  const normalized = (providerId ?? "").toLowerCase();
  if (normalized.includes("google")) return "google";
  if (normalized.includes("microsoft")) return "microsoft";
  if (normalized.includes("apple")) return "apple";
  return null;
};

const extractAppleDisplayNameFromCredential = (
  result: UserCredential,
): string | null => {
  if (result.providerId !== "apple.com") return null;

  const additionalUserInfo = getAdditionalUserInfo(result);
  const profile = (additionalUserInfo?.profile || null) as {
    name?: unknown;
    given_name?: unknown;
    family_name?: unknown;
    firstName?: unknown;
    lastName?: unknown;
  } | null;

  const profileName = profile?.name;
  if (typeof profileName === "string" && profileName.trim()) {
    return profileName.trim();
  }

  if (profileName && typeof profileName === "object") {
    const nameRecord = profileName as Record<string, unknown>;
    const firstName = getFirstNonEmptyString(
      nameRecord.firstName,
      nameRecord.givenName,
      nameRecord.given_name,
    );
    const lastName = getFirstNonEmptyString(
      nameRecord.lastName,
      nameRecord.familyName,
      nameRecord.family_name,
    );
    const combinedName = [firstName, lastName].filter(Boolean).join(" ").trim();
    if (combinedName) return combinedName;
  }

  return getFirstNonEmptyString(
    profile?.firstName,
    profile?.lastName,
    profile?.given_name,
    profile?.family_name,
  );
};

const persistAppleDisplayNameFromCredential = (
  result: UserCredential,
): string | null => {
  const appleDisplayName = extractAppleDisplayNameFromCredential(result);
  if (appleDisplayName) {
    sessionStorage.setItem(APPLE_SIGNUP_DISPLAY_NAME_KEY, appleDisplayName);
  }
  return appleDisplayName;
};

const resolveDisplayName = ({
  serverDisplayName,
  appleDisplayName,
  firebaseDisplayName,
  providerDisplayName,
  email,
}: {
  serverDisplayName?: string | null;
  appleDisplayName?: string | null;
  firebaseDisplayName?: string | null;
  providerDisplayName?: string | null;
  email?: string | null;
}): string =>
  getFirstNonEmptyString(
    serverDisplayName,
    appleDisplayName,
    firebaseDisplayName,
    providerDisplayName,
    getEmailLocalPart(email),
  ) || DEFAULT_DISPLAY_NAME;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [redirectSignInResult, setRedirectSignInResult] =
    useState<AuthRedirectSignInResult | null>(null);
  const [blockCheckVerified, setBlockCheckVerified] = useState<boolean>(false);
  const blockCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const processingAuthRef = useRef<boolean>(false); // 인증 처리 중인지 추적
  /** 서버에 마지막으로 동기화한 프로필 지문 — onAuthStateChanged 중복 시 불필요한 save 루프 방지 */
  const lastPersistedProfileRef = useRef<string | null>(null);
  const latestCredentialPhotoRef =
    useRef<AuthProviderProfileUpdatedDetail | null>(null);
  const sessionExpiryInProgressRef = useRef(false);

  const expireAuthSession = useCallback(
    async (reason: AuthSessionExpiryReason): Promise<void> => {
      if (sessionExpiryInProgressRef.current) return;
      sessionExpiryInProgressRef.current = true;

      if (reason === "absolute") {
        sessionStorage.setItem(
          "loginToast",
          JSON.stringify({
            type: "complete",
            message: "You've been signed out. Please sign in again.",
          }),
        );
      }

      if (blockCheckIntervalRef.current) {
        clearInterval(blockCheckIntervalRef.current);
        blockCheckIntervalRef.current = null;
      }
      sseService.disconnectAll();
      setBlockCheckVerified(false);
      setUser(null);
      setLoading(false);
      processingAuthRef.current = false;
      localStorage.removeItem("firebaseToken");
      localStorage.removeItem("guestUser");
      sessionStorage.removeItem("firebaseToken");
      clearAuthSession();

      try {
        await auth.signOut();
      } catch (signOutError) {
        console.error(
          "Firebase sign-out after session expiry failed:",
          signOutError,
        );
      }

      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      } else {
        sessionExpiryInProgressRef.current = false;
      }
    },
    [],
  );

  const handleSessionActivation = useCallback(async (): Promise<void> => {
    if (!readStoredAuthToken()) return;
    const reason = getAuthSessionExpiry();
    if (reason) {
      await expireAuthSession(reason);
      return;
    }
    recordAuthSessionActivity();
  }, [expireAuthSession]);

  useEffect(() => {
    const handleProviderProfileUpdated = (event: Event) => {
      const detail = (event as CustomEvent<AuthProviderProfileUpdatedDetail>)
        .detail;
      if (!detail?.uid || !detail.photoURL?.trim()) return;
      latestCredentialPhotoRef.current = detail;
      setUser((currentUser) =>
        currentUser?.uid === detail.uid
          ? { ...currentUser, photoURL: detail.photoURL }
          : currentUser,
      );
    };

    window.addEventListener(
      AUTH_PROVIDER_PROFILE_UPDATED_EVENT,
      handleProviderProfileUpdated,
    );
    return () => {
      window.removeEventListener(
        AUTH_PROVIDER_PROFILE_UPDATED_EVENT,
        handleProviderProfileUpdated,
      );
    };
  }, []);

  // 주기적으로 차단 여부 확인하는 함수
  const startPeriodicBlockCheck = useCallback(
    (token: string): void => {
      // 기존 인터벌이 있으면 정리
      if (blockCheckIntervalRef.current) {
        clearInterval(blockCheckIntervalRef.current);
      }

      // 30초마다 차단 여부 확인
      blockCheckIntervalRef.current = setInterval(async () => {
        const currentUser = user; // Capture current user value
        if (!currentUser) {
          // 사용자가 없으면 인터벌 정리
          if (blockCheckIntervalRef.current) {
            clearInterval(blockCheckIntervalRef.current);
            blockCheckIntervalRef.current = null;
          }
          return;
        }

        try {
          const currentToken =
            localStorage.getItem("firebaseToken") ||
            sessionStorage.getItem("firebaseToken");
          if (!currentToken) return;

          const userInfoResponse = await apiService.getUserInfo(currentToken);
          const isBlocked =
            (userInfoResponse as any)?.user?.isBlocked ||
            (userInfoResponse as any)?.isBlocked;

          if (isBlocked) {
            if (isDev) console.warn("인증 제공자: ì¬ì©ì ì°¨ë¨ ê°ì§ - ë¡ê·¸ìì");

            // 인터벌 정리
            if (blockCheckIntervalRef.current) {
              clearInterval(blockCheckIntervalRef.current);
              blockCheckIntervalRef.current = null;
            }

            // 차단 메시지를 sessionStorage에 저장
            sessionStorage.setItem(
              "loginToast",
              JSON.stringify({
                type: "error",
                message: BLOCKED_ACCOUNT_MESSAGE,
              }),
            );

            // 로그아웃 처리
            sseService.disconnectAll();
            setUser(null);
            localStorage.removeItem("firebaseToken");
            sessionStorage.removeItem("firebaseToken");

            // Firebase 로그아웃
            auth.signOut().catch((error) => {
              console.error("â Firebase ë¡ê·¸ìì ì¤í¨:", error);
            });

            // 로그인 페이지로 리디렉션
            if (window.location.pathname !== "/login") {
              window.location.href = "/login";
            }
          }
        } catch (error) {
          const errMsg = error instanceof Error ? error.message : String(error);
          const isSocialConflict =
            errMsg.includes("409") ||
            errMsg.includes(SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE);
          if (isSocialConflict) {
            if (blockCheckIntervalRef.current) {
              clearInterval(blockCheckIntervalRef.current);
              blockCheckIntervalRef.current = null;
            }
            sessionStorage.setItem(
              "loginToast",
              JSON.stringify({
                type: "error",
                message: SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE,
              }),
            );
            sseService.disconnectAll();
            setUser(null);
            localStorage.removeItem("firebaseToken");
            sessionStorage.removeItem("firebaseToken");
            auth.signOut().catch((signErr) => {
              console.error("Firebase 로그아웃 실패:", signErr);
            });
            if (window.location.pathname !== "/login") {
              window.location.href = "/login";
            }
          } else {
            console.error("â 인증 제공자: ì£¼ê¸°ì  ì°¨ë¨ íì¸ ì¤í¨:", error);
            // 오류 발생 시에도 계속 확인 (네트워크 오류 등으로 인한 경우)
          }
        }
      }, 30000); // 30초마다 확인
    },
    [user],
  );

  // Prod 등 Guest 비활성화 환경: 저장된 guest 토큰 제거 (401 방지)
  useEffect(() => {
    if (!isGuestAuthEnabled()) {
      if (isGuestSessionToken(readStoredAuthToken())) {
        localStorage.removeItem("firebaseToken");
        sessionStorage.removeItem("firebaseToken");
        localStorage.removeItem("guestUser");
        clearAuthSession();
      }
    }
  }, []);

  useEffect(() => {
    const handleFocus = () => void handleSessionActivation();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") handleFocus();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    handleFocus();
    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleSessionActivation]);

  useEffect(() => {
    // 앱 초기화 시 저장된 토큰으로 자동 로그인 복원 시도
    // 주의: loading 상태는 onAuthStateChanged에서만 제어함.
    // OAuth 리다이렉트 복귀 시 토큰이 아직 없어도 onAuthStateChanged가 user를 전달하므로,
    // 여기서 setLoading(false)를 호출하면 로그인 화면이 잠깐 보이는 버그 발생
    const restoreSessionFromToken = async (): Promise<void> => {
      try {
        const storedToken = readStoredAuthToken();

        if (!storedToken) {
          // loading은 onAuthStateChanged가 null/user로 콜백할 때만 변경
          return;
        }

        // Guest 세션은 Firebase 세션을 만들지 않으므로 Firebase가 복원해 주지 않는다.
        // 저장된 guest 토큰으로 직접 복구해야 새로고침에서 로그인 화면으로 튕기지 않는다.
        if (isGuestAuthEnabled() && isGuestSessionToken(storedToken)) {
          ensureStoredAuthSession();
          const expiryReason = getAuthSessionExpiry();
          if (expiryReason) {
            await expireAuthSession(expiryReason);
            return;
          }
          recordAuthSessionActivity();
          setUser((prevUser) =>
            prevUser?.isGuest ? prevUser : createGuestSessionUser(),
          );
          setLoading(false);
          return;
        }

        // Firebase Auth가 이미 세션을 복원했는지 확인
        if (auth.currentUser) {
          // onAuthStateChanged가 자동으로 처리
          return;
        }

        // 토큰이 있지만 Firebase 세션이 없는 경우
        // Firebase는 자동으로 세션을 복원하므로, 여기서는 토큰 검증만 수행
      } catch (error) {
        console.error("â AuthProvider: ì¸ì ë³μì ìë ì¤ ì¤ë¥:", error);
        // 오류 발생 시 토큰 제거
        localStorage.removeItem("firebaseToken");
        sessionStorage.removeItem("firebaseToken");
      }
    };

    // 초기 세션 복원 시도
    restoreSessionFromToken();

    const unsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser: FirebaseUser | null) => {
        // 중복 처리 방지: 이미 처리 중인 경우 스킵
        if (processingAuthRef.current) {
          return;
        }

        try {
          // console.log(
          //   "🔄 AuthProvider: 인증 상태 변경됨:",
          //   firebaseUser ? "로그인됨" : "로그아웃됨"
          // );

          // WebView 환경 감지 로그
          if (isWebView()) {
            if (isDev) console.log("WebView: ì¸ì¦ ìí ë³ê²½");
          }

          if (firebaseUser) {
            sessionStorage.removeItem(ACCOUNT_EXISTS_MS_REDIRECT_RECOVERY_KEY);

            // 인증 처리 시작 표시 - 로그인 화면이 리디렉션 전까지 보이지 않도록 즉시 loading 표시
            processingAuthRef.current = true;
            setLoading(true);

            // 사용자 정보 구조화
            const providerId =
              firebaseUser.providerData[0]?.providerId || "unknown";
            const resolvedEmail = getFirebaseResolvedEmail(firebaseUser);
            const providerDisplayName =
              getFirebaseProviderDisplayName(firebaseUser);
            const pendingAppleDisplayName = providerId.includes("apple")
              ? getPendingAppleDisplayName()
              : null;
            const providerName: LoginMethod = providerId.includes("google")
              ? "google"
              : providerId.includes("apple")
                ? "apple"
                : providerId.includes("microsoft")
                  ? "microsoft"
                  : providerId.includes("password")
                    ? "email"
                    : "guest";

            const userInfo: User = {
              uid: firebaseUser.uid,
              email: resolvedEmail,
              displayName: resolveDisplayName({
                appleDisplayName: pendingAppleDisplayName,
                firebaseDisplayName: firebaseUser.displayName,
                providerDisplayName,
                email: resolvedEmail,
              }),
              photoURL: firebaseUser.photoURL || null,
              token: "", // Will be set below
              providerData: firebaseUser.providerData.map((data) => ({
                providerId: data.providerId,
                uid: data.uid,
                displayName: data.displayName || null,
                email: data.email || null,
                photoURL: data.photoURL || null,
              })),
              isGuest: false,
            };

            // Desktop 소셜 pairing은 Web Studio 세션을 여는 흐름이 아니다.
            // PairPage가 Firebase redirect 결과를 검증할 수 있도록 화면만
            // 해제하고, Web user 또는 저장 token은 발행하지 않는다.
            // 실제 완료 권한은 PairPage의 session별 attempt + provider + UID
            // + redirect 시각 검증과 Main API consumer key가 계속 소유한다.
            if (isDesktopSocialPairingLocation()) {
              setLoading(false);
              processingAuthRef.current = false;
              return;
            }

            const now = Date.now();
            const firebaseStartedAt = Date.parse(
              firebaseUser.metadata.lastSignInTime || "",
            );
            ensureAuthSession(
              Number.isFinite(firebaseStartedAt) ? firebaseStartedAt : now,
              now,
            );
            const expiryReason = getAuthSessionExpiry(now);
            if (expiryReason) {
              await expireAuthSession(expiryReason);
              return;
            }
            recordAuthSessionActivity(now);

            // Firebase 토큰 가져오기 (자동 갱신 포함)
            // forceRefresh=false: 만료 전이면 캐시된 토큰 반환, 만료 직전이면 자동 갱신
            const token = await firebaseUser.getIdToken(false);

            // 토큰을 localStorage에 저장 (영구 저장)
            localStorage.setItem("firebaseToken", token);

            // sessionStorage에도 백업 저장 (일부 환경에서 필요할 수 있음)
            sessionStorage.setItem("firebaseToken", token);

            // WebView용 UserInfo 생성 (provider 필드 필요)
            const webViewUserInfo = {
              uid: userInfo.uid,
              email: userInfo.email,
              displayName: userInfo.displayName,
              provider: providerName,
              token,
            };

            const isNativeAvpAuthWebView = isViewRNativeAuthWebView();

            // Generic embedded clients keep their existing handoff timing. AVP must
            // not receive AUTH_SUCCESS until Main API confirms full access.
            if (!isNativeAvpAuthWebView) {
              handleWebViewLoginSuccess(token, webViewUserInfo);
            }

            if (isWebView() && !isNativeAvpAuthWebView) {
              try {
                await notifyAuthSuccessToServer(token, webViewUserInfo);
              } catch (error) {
                console.error("â WebView ìë² ìë¦¼ ì¤í¨:", error);
              }
            }

            // 차단 확인을 위해 먼저 서버 프로필 조회 → displayName 레이스(save가 이메일 앞부분으로 덮어쓰는 현상) 방지
            let userInfoResponse: unknown = null;
            try {
              userInfoResponse = await apiService.getUserInfo(token);
            } catch (getErr) {
              const errMsg =
                getErr instanceof Error ? getErr.message : String(getErr);
              const is503 = errMsg.includes("503") || errMsg.includes("502");
              const isSocialConflict =
                errMsg.includes("409") ||
                errMsg.includes(SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE);
              console.error("AuthProvider: 사용자 정보 조회 실패:", getErr);
              if (isSocialConflict || errMsg.includes("409")) {
                const payload = parseSocialConflict409Payload(
                  getErr instanceof Error ? getErr : new Error(String(getErr)),
                );
                if (payload?.stored_login_method && auth.currentUser) {
                  try {
                    await unlinkSessionProviderIfNotCanonical(
                      auth.currentUser,
                      payload.stored_login_method,
                    );
                  } catch {
                    // ignore
                  }
                }
              }
              const toastMessage = is503
                ? SERVICE_UNAVAILABLE_MESSAGE
                : isSocialConflict
                  ? SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE
                  : USER_INFO_CHECK_FAILED_MESSAGE;
              sessionStorage.setItem(
                "loginToast",
                JSON.stringify({
                  type: "error",
                  message: toastMessage,
                }),
              );
              sseService.disconnectAll();
              setUser(null);
              setLoading(false);
              processingAuthRef.current = false;
              localStorage.removeItem("firebaseToken");
              sessionStorage.removeItem("firebaseToken");
              auth.signOut().catch((error) => {
                console.error("Firebase 로그아웃 실패:", error);
              });
              if (window.location.pathname !== "/login") {
                window.location.href = "/login";
              }
              return;
            }

            const mergedDisplayNameForSave = resolveDisplayName({
              serverDisplayName: getServerDisplayName(userInfoResponse),
              appleDisplayName: pendingAppleDisplayName,
              firebaseDisplayName: firebaseUser.displayName,
              providerDisplayName,
              email: resolvedEmail,
            });
            const serverPhotoURL = getServerPhotoURL(userInfoResponse);
            const credentialPhotoURL =
              latestCredentialPhotoRef.current?.uid === firebaseUser.uid
                ? latestCredentialPhotoRef.current.photoURL
                : null;
            let mergedPhotoURLForSave = resolveAuthProfilePhotoURL({
              credentialPhotoURL,
              firebasePhotoURL: firebaseUser.photoURL,
              serverPhotoURL,
              providerPhotoURLs: firebaseUser.providerData.map(
                (data) => data.photoURL,
              ),
            });

            try {
              await firebaseUser.reload();
              mergedPhotoURLForSave = resolveAuthProfilePhotoURL({
                credentialPhotoURL:
                  latestCredentialPhotoRef.current?.uid === firebaseUser.uid
                    ? latestCredentialPhotoRef.current.photoURL
                    : credentialPhotoURL,
                firebasePhotoURL: firebaseUser.photoURL,
                serverPhotoURL,
                providerPhotoURLs: firebaseUser.providerData.map(
                  (data) => data.photoURL,
                ),
              });
            } catch (reloadErr) {
              if (isDev)
                console.warn("인증: Firebase 프로필 갱신 실패", reloadErr);
            }

            // A Firebase identity alone is not a Studio account. Keep the
            // service user profile out of `users` until the admission API has
            // confirmed an approved membership (or server-side Super Admin).
            let hasApprovedStudioAccess = false;
            try {
              const access = await apiService.getAccessStatus();
              hasApprovedStudioAccess =
                access.accessStatus === "approved" &&
                access.permissionScope === "full";
            } catch (accessError) {
              if (isDev) {
                console.warn(
                  "인증: Studio 접근 상태 확인 실패, 사용자 정보 저장 보류",
                  accessError,
                );
              }
            }

            if (isNativeAvpAuthWebView && hasApprovedStudioAccess) {
              handleWebViewLoginSuccess(token, webViewUserInfo);
              try {
                await notifyAuthSuccessToServer(token, webViewUserInfo);
              } catch (error) {
                console.error(
                  "AVP WebView server auth notification failed:",
                  error,
                );
              }
            }

            const persistFingerprint = `${firebaseUser.uid}|${mergedDisplayNameForSave ?? ""}|${mergedPhotoURLForSave ?? ""}|${providerName}`;
            if (
              hasApprovedStudioAccess &&
              lastPersistedProfileRef.current !== persistFingerprint
            ) {
              try {
                // JWT에 email 클레임이 늦게 붙는 경우가 있어, 저장 직전에 토큰 갱신
                const saveToken = await firebaseUser.getIdToken(true);
                localStorage.setItem("firebaseToken", saveToken);
                sessionStorage.setItem("firebaseToken", saveToken);
                const emailAfterReload = getFirebaseResolvedEmail(firebaseUser);
                const userInfoForSave = {
                  ...userInfo,
                  email: emailAfterReload,
                  photoURL: mergedPhotoURLForSave,
                  providerData: firebaseUser.providerData.map((data) => ({
                    providerId: data.providerId,
                    uid: data.uid,
                    displayName: data.displayName || null,
                    email: data.email || null,
                    photoURL: data.photoURL || null,
                  })),
                };
                await apiService.saveUserInfo(
                  {
                    ...userInfoForSave,
                    displayName: mergedDisplayNameForSave,
                    photoURL: mergedPhotoURLForSave,
                    provider: providerName,
                    loginMethod: providerName,
                    emailVerified: firebaseUser.emailVerified,
                    createdAt: firebaseUser.metadata?.creationTime ?? "",
                    lastSignInTime: firebaseUser.metadata?.lastSignInTime ?? "",
                    token: saveToken,
                  },
                  saveToken,
                );
                lastPersistedProfileRef.current = persistFingerprint;
              } catch (saveErr) {
                const errorMessage =
                  saveErr instanceof Error ? saveErr.message : String(saveErr);
                if (
                  errorMessage.includes("409") ||
                  errorMessage.includes(SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE)
                ) {
                  const payload = parseSocialConflict409Payload(
                    saveErr instanceof Error
                      ? saveErr
                      : new Error(String(saveErr)),
                  );
                  if (payload?.stored_login_method && auth.currentUser) {
                    try {
                      await unlinkSessionProviderIfNotCanonical(
                        auth.currentUser,
                        payload.stored_login_method,
                      );
                    } catch {
                      // ignore
                    }
                  }
                  sessionStorage.setItem(
                    "loginToast",
                    JSON.stringify({
                      type: "error",
                      message: SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE,
                    }),
                  );
                  sseService.disconnectAll();
                  setUser(null);
                  setLoading(false);
                  processingAuthRef.current = false;
                  lastPersistedProfileRef.current = null;
                  localStorage.removeItem("firebaseToken");
                  sessionStorage.removeItem("firebaseToken");
                  await auth.signOut();
                  if (window.location.pathname !== "/login") {
                    window.location.href = "/login";
                  }
                  return;
                }
                if (
                  errorMessage.includes("duplicate key value") ||
                  errorMessage.includes("already exists")
                ) {
                  if (isDev) console.warn("인증: 중복 키/이메일 충돌");
                } else {
                  if (isDev)
                    console.warn("인증: /api/users/save 실패, 로그인은 유지");
                }
              }
            }

            // 응답이 없으면 차단 확인 불가 - 로그인 차단
            if (!userInfoResponse) {
              console.error(
                "â AuthProvider: ì¬ì©ì ì ë³´ ìëμ ìì - ë¡ê·¸ì¸ ì°¨ë¨",
              );
              sessionStorage.setItem(
                "loginToast",
                JSON.stringify({
                  type: "error",
                  message: USER_INFO_UNAVAILABLE_MESSAGE,
                }),
              );
              sseService.disconnectAll();
              setUser(null);
              setLoading(false); // loading 해제하여 PublicRoute가 로그인 페이지를 표시할 수 있도록
              processingAuthRef.current = false; // 처리 완료 표시
              localStorage.removeItem("firebaseToken");
              sessionStorage.removeItem("firebaseToken");
              auth.signOut().catch((error) => {
                console.error("â Firebase ë¡ê·¸ìì ì¤í¨:", error);
              });
              if (window.location.pathname !== "/login") {
                window.location.href = "/login";
              }
              return;
            }

            // isBlocked 값 확인 (여러 경로로 확인)
            const isBlocked =
              (userInfoResponse as any)?.user?.isBlocked === true ||
              (userInfoResponse as any)?.user?.isBlocked === "true" ||
              (userInfoResponse as any)?.isBlocked === true ||
              (userInfoResponse as any)?.isBlocked === "true";

            if (isBlocked) {
              if (isDev)
                console.warn("인증 제공자: ì¬ì©ì ì°¨ë¨ë¨", {
                  uid: firebaseUser.uid,
                  email: firebaseUser.email,
                  isBlocked,
                });

              sessionStorage.setItem(
                "loginToast",
                JSON.stringify({
                  type: "error",
                  message: BLOCKED_ACCOUNT_MESSAGE,
                }),
              );

              sseService.disconnectAll();
              setUser(null);
              setLoading(false);
              processingAuthRef.current = false;
              localStorage.removeItem("firebaseToken");
              sessionStorage.removeItem("firebaseToken");
              auth.signOut().catch((error) => {
                console.error("â Firebase ë¡ê·¸ìì ì¤í¨:", error);
              });
              if (window.location.pathname !== "/login") {
                window.location.href = "/login";
              }
              return;
            }

            setBlockCheckVerified(true);

            // 객체 참조 변경 방지: 이전 상태와 비교하여 실제로 변경된 경우에만 업데이트
            const newUser: User = {
              ...userInfo,
              displayName: resolveDisplayName({
                serverDisplayName: getServerDisplayName(userInfoResponse),
                appleDisplayName: pendingAppleDisplayName,
                firebaseDisplayName: firebaseUser.displayName,
                providerDisplayName,
                email: resolvedEmail,
              }),
              photoURL: resolveAuthProfilePhotoURL({
                credentialPhotoURL:
                  latestCredentialPhotoRef.current?.uid === firebaseUser.uid
                    ? latestCredentialPhotoRef.current.photoURL
                    : credentialPhotoURL,
                firebasePhotoURL: mergedPhotoURLForSave,
                providerPhotoURLs: firebaseUser.providerData.map(
                  (data) => data.photoURL,
                ),
                serverPhotoURL,
              }),
              token,
            };

            try {
              await syncFirebaseDisplayName(firebaseUser, newUser.displayName);
            } catch (error) {
              console.error("â Firebase 디스플레이 이름 ëê¸°í ì¤í¨:", error);
            }

            // setUser와 setLoading을 동시에 호출하여 같은 렌더 사이클에서 처리되도록 함
            // React 18의 자동 배치 처리를 활용
            setUser((prevUser) => {
              // 이전 사용자와 비교하여 실제로 변경된 경우에만 업데이트
              if (
                prevUser &&
                prevUser.uid === newUser.uid &&
                prevUser.email === newUser.email &&
                prevUser.token === newUser.token &&
                prevUser.displayName === newUser.displayName &&
                prevUser.photoURL === newUser.photoURL &&
                prevUser.isGuest === newUser.isGuest
              ) {
                // 내용이 동일하면 이전 객체 참조 유지 (리렌더링 방지)
                return prevUser;
              }
              // 내용이 다르면 새 객체 반환
              return newUser;
            });

            // setUser와 동시에 setLoading(false) 호출
            // React 18의 자동 배치 처리로 인해 같은 렌더 사이클에서 처리됨
            setLoading(false);

            if (providerName === "apple" && pendingAppleDisplayName) {
              clearPendingAppleDisplayName();
            }

            // 인증 처리 완료 표시
            processingAuthRef.current = false;

            // 주기적으로 차단 여부 확인 시작 (로그인한 상태에서 차단 당했을 때 대응)
            startPeriodicBlockCheck(token);

            // WebView 환경에서 추가 토큰 반환 시도
            if (isWebView() && !isNativeAvpAuthWebView) {
              if (isDev) console.log("WebView: ì¶ê° í í° ë°í ìë");
              // 약간의 지연 후 다시 시도
              setTimeout(() => {
                handleWebViewLoginSuccess(token, webViewUserInfo);
              }, 1000);

              // 강제 토큰 추출 시도 (Promise rejection 처리로 Uncaught 방지)
              setTimeout(() => {
                forceExtractFirebaseToken().catch((err) => {
                  if (isDev) console.warn("WebView ê°ì  í í° ì¶ì¶ ì¤í¨:", err);
                });
              }, 3000);
            }
          } else {
            setBlockCheckVerified(false);
            lastPersistedProfileRef.current = null;
            latestCredentialPhotoRef.current = null;

            // Guest 사용자인 경우 Firebase 로그아웃 상태를 무시하고 유지
            setUser((currentUser) => {
              if (currentUser?.isGuest) {
                return currentUser; // Guest 사용자 상태 유지
              }

              // 일반 사용자는 로그아웃 처리
              // 단, 저장된 토큰이 있고 Firebase 세션이 일시적으로 끊어진 경우를 대비해
              // 토큰은 유지하고 Firebase가 자동으로 복원할 때까지 대기
              const storedToken = readStoredAuthToken();

              // 새로고침 직후에는 React state가 비어 있어 currentUser로 guest를 판별할 수 없다.
              // Guest는 Firebase 세션이 없어 여기로 항상 들어오므로 저장된 토큰으로 판별한다.
              if (
                !currentUser &&
                isGuestAuthEnabled() &&
                isGuestSessionToken(storedToken)
              ) {
                return createGuestSessionUser();
              }

              if (storedToken && !currentUser) {
                // Firebase가 자동으로 세션을 복원할 수 있으므로 토큰은 유지
                // onAuthStateChanged가 다시 호출되면 자동으로 복원됨
              } else {
                // 명시적인 로그아웃인 경우 토큰 제거
                if (blockCheckIntervalRef.current) {
                  clearInterval(blockCheckIntervalRef.current);
                  blockCheckIntervalRef.current = null;
                }
                sseService.disconnectAll();
                localStorage.removeItem("firebaseToken");
                sessionStorage.removeItem("firebaseToken");
              }

              return null;
            });
          }

          setError(null);

          // 로그아웃 상태인 경우 loading 해제
          if (!firebaseUser) {
            setLoading(false);
            processingAuthRef.current = false;
          }
          // 로그인 상태인 경우는 setUser 호출 후에 이미 setLoading(false)가 호출됨
        } catch (error) {
          console.error("â AuthProvider: ì¸ì¦ ìí ì²ë¦¬ ì¤ ì¤ë¥:", error);
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          setError(errorMessage);
          setUser(null);
          setLoading(false); // 에러 발생 시 즉시 loading 해제
          processingAuthRef.current = false; // 에러 발생 시 처리 완료 표시
        }
      },
    );

    // 리디렉션 결과 처리
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);

        if (result) {
          const redirectProvider = resolveSocialLoginMethod(
            result.providerId || result.user.providerData[0]?.providerId,
          );
          if (redirectProvider) {
            setRedirectSignInResult({
              uid: result.user.uid,
              provider: redirectProvider,
              completedAt: Date.now(),
            });
          }
          clearPendingSocialRedirectProvider();

          const appleDisplayName =
            persistAppleDisplayNameFromCredential(result);

          // 계정 삭제를 위한 재인증 리디렉션 복귀 처리
          const pendingDeleteStr = sessionStorage.getItem(
            "pendingDeleteAccount",
          );
          if (pendingDeleteStr) {
            try {
              sessionStorage.removeItem("pendingDeleteAccount");
              const payload = JSON.parse(pendingDeleteStr) as {
                reasons?: string[];
                comments?: string;
              };
              const token = await result.user.getIdToken();
              // 서버 정리가 실패하면 Firebase 삭제를 진행하지 않습니다.
              await apiService.deleteAccount(
                {
                  reasons: payload?.reasons || [],
                  comments: payload?.comments || "",
                },
                token,
              );
              await deleteUser(result.user);
              sseService.disconnectAll();
              localStorage.removeItem("firebaseToken");
              sessionStorage.removeItem("firebaseToken");
              sessionStorage.setItem(
                "loginToast",
                JSON.stringify({
                  type: "complete",
                  message: "Account deleted.",
                }),
              );
              await signOut(auth);
              window.location.href = "/login";
              return;
            } catch (delErr) {
              console.error("…", delErr);
            }
          }

          try {
            await syncAuthProviderProfileFromCredential(result);
          } catch (profileError) {
            console.warn(
              "AuthProvider: provider profile photo sync failed:",
              profileError,
            );
          }

          // WebView 환경에서 즉시 토큰 반환 처리
          if (isWebView() && !isViewRNativeAuthWebView()) {
            try {
              const token = await result.user.getIdToken();
              const providerId =
                result.user.providerData[0]?.providerId || "unknown";
              const providerName: LoginMethod = providerId.includes("google")
                ? "google"
                : providerId.includes("apple")
                  ? "apple"
                  : providerId.includes("microsoft")
                    ? "microsoft"
                    : providerId.includes("password")
                      ? "email"
                      : "guest";

              const webViewUserInfo = {
                uid: result.user.uid,
                email: result.user.email || null,
                displayName: resolveDisplayName({
                  firebaseDisplayName: result.user.displayName,
                  providerDisplayName: getFirebaseProviderDisplayName(
                    result.user,
                  ),
                  email: getFirebaseResolvedEmail(result.user),
                }),
                provider: providerName,
                token,
              };

              if (isDev) console.log("WebView: í í° ë°í");
              handleWebViewLoginSuccess(token, webViewUserInfo);
              await notifyAuthSuccessToServer(token, webViewUserInfo);
            } catch (error) {
              console.error("â WebView ë¦¬ëë ì í í° ì²ë¦¬ ì¤í¨:", error);
            }
          }

          if (appleDisplayName) {
            try {
              const access = await apiService.getAccessStatus();
              // Keep Apple profile data in session until the account is
              // approved. Saving it here would create a service user before
              // the access-request decision.
              if (
                access.accessStatus !== "approved" ||
                access.permissionScope !== "full"
              )
                return;

              await result.user.reload();
              const token = await result.user.getIdToken(true);
              const resolvedEmail = getFirebaseResolvedEmail(result.user);
              const providerDisplayName = getFirebaseProviderDisplayName(
                result.user,
              );
              const normalizedDisplayName = resolveDisplayName({
                appleDisplayName,
                firebaseDisplayName: result.user.displayName,
                providerDisplayName,
                email: resolvedEmail,
              });
              const providerPhotoURLs = result.user.providerData.map(
                (data) => data.photoURL,
              );
              const mergedPhotoURLForSave = resolveAuthProfilePhotoURL({
                credentialPhotoURL:
                  latestCredentialPhotoRef.current?.uid === result.user.uid
                    ? latestCredentialPhotoRef.current.photoURL
                    : null,
                firebasePhotoURL: result.user.photoURL,
                providerPhotoURLs,
              });

              await syncFirebaseDisplayName(result.user, normalizedDisplayName);

              const saveUserInfoResponse = await apiService.saveUserInfo(
                {
                  uid: result.user.uid,
                  email: resolvedEmail,
                  displayName: normalizedDisplayName,
                  photoURL: mergedPhotoURLForSave,
                  token,
                  providerData: result.user.providerData.map((data) => ({
                    providerId: data.providerId,
                    uid: data.uid,
                    displayName: data.displayName || null,
                    email: data.email || null,
                    photoURL: data.photoURL || null,
                  })),
                  isGuest: false,
                },
                token,
              );

              const userInfoResponse = await apiService.getUserInfo(token);
              const mergedPhotoURLForState = resolveAuthProfilePhotoURL({
                credentialPhotoURL:
                  latestCredentialPhotoRef.current?.uid === result.user.uid
                    ? latestCredentialPhotoRef.current.photoURL
                    : null,
                firebasePhotoURL: result.user.photoURL,
                serverPhotoURL:
                  getServerPhotoURL(userInfoResponse) ||
                  getServerPhotoURL(saveUserInfoResponse),
                providerPhotoURLs,
              });

              setUser((prevUser) =>
                prevUser && prevUser.uid === result.user.uid
                  ? {
                      ...prevUser,
                      displayName: resolveDisplayName({
                        serverDisplayName:
                          getServerDisplayName(userInfoResponse),
                        appleDisplayName,
                        firebaseDisplayName: result.user.displayName,
                        providerDisplayName,
                        email: resolvedEmail,
                      }),
                      photoURL: mergedPhotoURLForState,
                    }
                  : prevUser,
              );
              clearPendingAppleDisplayName();
            } catch (error) {
              console.error("â Apple 디스플레이 이름 ì ì¥ ì¤í¨:", error);
            }
          }
        } else {
          clearPendingSocialRedirectProvider();
          // 리디렉션 결과가 없고, URL에 에러 파라미터가 있는 경우 에러 설정
          const urlParams = new URLSearchParams(window.location.search);
          const errorCode = urlParams.get("error");
          const errorDescription = urlParams.get("error_description");

          if (errorCode || errorDescription) {
            const decodedDesc = errorDescription
              ? decodeURIComponent(errorDescription.replace(/\+/g, " "))
              : "";
            const oauthErrRaw = `${errorCode ?? ""} ${decodedDesc}`.trim();
            let errorMessage =
              mapSocialEmailConflictMessage(oauthErrRaw) ===
              SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE
                ? SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE
                : "An authentication error occurred. Please try again later.";

            if (
              errorMessage !== SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE &&
              errorDescription
            ) {
              const d = decodedDesc.toLowerCase();
              if (d.includes("google")) {
                errorMessage = "Google sign-in failed. Please try again.";
              } else if (d.includes("apple")) {
                errorMessage = "Apple sign-in failed. Please try again.";
              } else if (d.includes("microsoft")) {
                errorMessage = "Microsoft sign-in failed. Please try again.";
              }
            }

            if (isDev)
              console.warn("인증 제공자: URL ìë¬:", {
                errorCode,
                errorDescription,
              });
            setError(errorMessage);

            // URL에서 에러 파라미터 제거
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
          }

          // WebView 환경에서 리디렉션 결과가 없어도 강제 추출 시도
          if (isWebView()) {
            if (isDev) console.log("WebView: ê°ì  ì¶ì¶ ìë");
            setTimeout(() => {
              forceExtractFirebaseToken().catch((err) => {
                if (isDev) console.warn("WebView ê°ì  ì¶ì¶ ì¤í¨:", err);
              });
            }, 2000);
          }
        }
      } catch (error) {
        void (async () => {
          const recovered =
            await tryRecoverMicrosoftRedirectAfterAccountExistsError(error);
          if (recovered) {
            return;
          }
          console.error("â 인증 제공자: ë¦¬ëë ì ê²°ê3¼ ì²ë¦¬ ì¤ ì¤ë¥:", error);
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          const pendingProvider = getPendingSocialRedirectProvider();
          clearPendingSocialRedirectProvider();
          setError(
            mapLoginErrorMessage(
              pendingProvider
                ? `${pendingProvider} redirect sign-in failed: ${errorMessage}`
                : errorMessage,
            ),
          );

          // WebView 환경에서 오류 발생 시에도 강제 추출 시도
          if (isWebView()) {
            if (isDev) console.log("WebView: ì¤ë¥ ì ê°ì  ì¶ì¶");
            setTimeout(() => {
              forceExtractFirebaseToken().catch((err) => {
                if (isDev) console.warn("WebView ê°ì  ì¶ì¶ ì¤í¨:", err);
              });
            }, 2000);
          }
        })();
      }
    };

    // 컴포넌트 마운트 시 리디렉션 결과 확인 (Promise 미처리 시 Uncaught (in promise) 방지)
    handleRedirectResult().catch((err: unknown) => {
      void (async () => {
        const recovered =
          await tryRecoverMicrosoftRedirectAfterAccountExistsError(err);
        if (recovered) {
          return;
        }
        console.error("â 인증 제공자: ë¦¬ëë ì ê²°ê3¼ ì²ë¦¬ ì¤ ì¤ë¥:", err);
        const errorMessage = err instanceof Error ? err.message : String(err);
        const pendingProvider = getPendingSocialRedirectProvider();
        clearPendingSocialRedirectProvider();
        setError(
          mapLoginErrorMessage(
            pendingProvider
              ? `${pendingProvider} redirect sign-in failed: ${errorMessage}`
              : errorMessage,
          ),
        );
        if (isWebView()) {
          setTimeout(() => {
            forceExtractFirebaseToken().catch((e) => {
              if (isDev) console.warn("WebView ê°ì  ì¶ì¶ ì¤í¨:", e);
            });
          }, 2000);
        }
      })();
    });

    return unsubscribe;
  }, [expireAuthSession, startPeriodicBlockCheck]);

  // ✅ 추가: Guest 로그인 함수
  const loginAsGuestUser = useCallback(
    async (provider: string = "guest"): Promise<void> => {
      if (!isGuestAuthEnabled()) {
        throw new Error("Guest sign-in is not available.");
      }

      const guestUser: User = createGuestSessionUser();

      // 객체 참조 변경 방지: 이전 상태와 비교하여 실제로 변경된 경우에만 업데이트
      setUser((prevUser) => {
        // 이전 사용자와 비교하여 실제로 변경된 경우에만 업데이트
        if (
          prevUser &&
          prevUser.uid === guestUser.uid &&
          prevUser.email === guestUser.email &&
          prevUser.token === guestUser.token &&
          prevUser.displayName === guestUser.displayName &&
          prevUser.photoURL === guestUser.photoURL &&
          prevUser.isGuest === guestUser.isGuest
        ) {
          // 내용이 동일하면 이전 객체 참조 유지 (리렌더링 방지)
          return prevUser;
        }
        // 내용이 다르면 새 객체 반환
        return guestUser;
      });
      // Guest 토큰 저장
      localStorage.setItem("firebaseToken", guestUser.token);
      beginAuthSession(Date.now());

      // WebView용 UserInfo 생성
      const guestWebViewUserInfo = {
        uid: guestUser.uid,
        email: guestUser.email || null,
        displayName: guestUser.displayName || null,
        provider: "guest",
        login_method: "guest" as LoginMethod,
        isGuest: true,
        token: guestUser.token,
      };

      // WebView 환경에서 guest 로그인 토큰 반환 처리
      handleWebViewLoginSuccess(guestUser.token, guestWebViewUserInfo);

      // WebView 환경에서 서버에 guest 로그인 성공 알림
      if (isWebView()) {
        try {
          await notifyAuthSuccessToServer(
            guestUser.token,
            guestWebViewUserInfo,
          );
        } catch (error) {
          console.error("â WebView 게스트 ë¡ê·¸ì¸ ìë² ìë¦¼ ì¤í¨:", error);
        }
      }
    },
    [],
  );

  // 차단 여부 확인 함수 (외부에서 호출 가능)
  const checkUserBlocked = useCallback(async (): Promise<boolean> => {
    if (!user) return false;

    try {
      const token =
        localStorage.getItem("firebaseToken") ||
        sessionStorage.getItem("firebaseToken");
      if (!token) return false;

      const userInfoResponse = await apiService.getUserInfo(token);
      const isBlocked =
        (userInfoResponse as any)?.user?.isBlocked ||
        (userInfoResponse as any)?.isBlocked;

      if (isBlocked) {
        if (isDev) console.warn("인증 제공자: ì°¨ë¨ íì¸ - ë¡ê·¸ìì");

        // 인터벌 정리
        if (blockCheckIntervalRef.current) {
          clearInterval(blockCheckIntervalRef.current);
          blockCheckIntervalRef.current = null;
        }

        // 차단 메시지를 sessionStorage에 저장
        sessionStorage.setItem(
          "loginToast",
          JSON.stringify({
            type: "error",
            message: BLOCKED_ACCOUNT_MESSAGE,
          }),
        );

        // 로그아웃 처리
        sseService.disconnectAll();
        setBlockCheckVerified(false);
        setUser(null);
        localStorage.removeItem("firebaseToken");
        sessionStorage.removeItem("firebaseToken");

        // Firebase 로그아웃
        auth.signOut().catch((error) => {
          console.error("â Firebase ë¡ê·¸ìì ì¤í¨:", error);
        });

        // 로그인 페이지로 리디렉션
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }

        return true;
      }
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : String(error);
      const isSocialConflict =
        errMsg.includes("409") ||
        errMsg.includes(SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE);
      if (isSocialConflict) {
        if (blockCheckIntervalRef.current) {
          clearInterval(blockCheckIntervalRef.current);
          blockCheckIntervalRef.current = null;
        }
        sessionStorage.setItem(
          "loginToast",
          JSON.stringify({
            type: "error",
            message: SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE,
          }),
        );
        sseService.disconnectAll();
        setBlockCheckVerified(false);
        setUser(null);
        localStorage.removeItem("firebaseToken");
        sessionStorage.removeItem("firebaseToken");
        auth.signOut().catch((signErr) => {
          console.error("Firebase 로그아웃 실패:", signErr);
        });
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return true;
      }
      console.error("â 인증 제공자: ì°¨ë¨ íì¸ ì¤í¨:", error);
    }

    return false;
  }, [user]);

  // ✅ 추가: 로그아웃 함수
  const logout = useCallback(async (): Promise<void> => {
    sseService.disconnectAll();

    // 인터벌 정리
    if (blockCheckIntervalRef.current) {
      clearInterval(blockCheckIntervalRef.current);
      blockCheckIntervalRef.current = null;
    }

    // 저장된 토큰을 먼저 지운다. signOut이 트리거하는 onAuthStateChanged가
    // 남아 있는 guest 토큰을 보고 세션을 되살리지 않도록 하기 위함이다.
    localStorage.removeItem("firebaseToken");
    sessionStorage.removeItem("firebaseToken");
    clearAuthSession();

    try {
      await auth.signOut();
    } catch (error) {
      console.error("â Firebase ë¡ê·¸ìì ì¤í¨:", error);
    } finally {
      setBlockCheckVerified(false);
      setUser(null);
      localStorage.removeItem("firebaseToken");
      sessionStorage.removeItem("firebaseToken");
      clearAuthSession();
    }
  }, []);

  // context value를 메모이제이션하여 불필요한 리렌더링 방지
  const value: AuthContextValue = useMemo(
    () => ({
      user,
      loading,
      error,
      isAuthenticated: !!user,
      redirectSignInResult,
      blockCheckVerified,
      loginAsGuestUser,
      logout,
      setError,
      checkUserBlocked, // 차단 여부 확인 함수 export
    }),
    [
      user,
      loading,
      error,
      redirectSignInResult,
      blockCheckVerified,
      loginAsGuestUser,
      logout,
      checkUserBlocked,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
